import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// Helper to get days in month
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Helper to get first day of week (0=Sun, 1=Mon...)
function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

// Helper to format date as yyyy-mm-dd
function formatDate(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function OrderCalendar() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://teenagefashion.in/backend/api' 
      : 'http://localhost/newfashion/backend/api');

  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });
  const [orderCounts, setOrderCounts] = useState({});
  const [orderCountsLoading, setOrderCountsLoading] = useState(true);

  // Function to fetch order counts for a specific month range (only pending and in-progress orders)
  const fetchOrderCountsForMonth = useCallback((year, month) => {
    setOrderCountsLoading(true);
    const startDate = new Date(year, month - 1, 1); // Previous month start (local)
    const endDate = new Date(year, month + 1, 0);   // Current month end (local)

    // Build local YYYY-MM-DD strings to avoid UTC shift
    const startStr = formatDate(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    );
    const endStr = formatDate(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    );
    
    fetch(`${API_URL}/order_day_count.php?start_date=${startStr}&end_date=${endStr}&status_filter=Pending,In Progress`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setOrderCounts(data.data || {});
        setOrderCountsLoading(false);
      })
      .catch(() => setOrderCountsLoading(false));
  }, [API_URL]);

  useEffect(() => {
    // Fetch order counts for current month
    const today = new Date();
    fetchOrderCountsForMonth(today.getFullYear(), today.getMonth());
  }, [fetchOrderCountsForMonth]);

  const handleMonthChange = (direction) => {
    const newMonth = direction === 'prev' 
      ? (calendarMonth.month === 0 ? 11 : calendarMonth.month - 1)
      : (calendarMonth.month === 11 ? 0 : calendarMonth.month + 1);
    const newYear = direction === 'prev'
      ? (calendarMonth.month === 0 ? calendarMonth.year - 1 : calendarMonth.year)
      : (calendarMonth.month === 11 ? calendarMonth.year + 1 : calendarMonth.year);
    
    setCalendarMonth({ year: newYear, month: newMonth });
    fetchOrderCountsForMonth(newYear, newMonth);
  };

  const getOrderCountColor = (count) => {
    if (count === 0) return 'bg-gray-100 text-gray-400';
    if (count <= 3) return 'bg-blue-500 text-white';
    if (count <= 6) return 'bg-orange-500 text-white';
    return 'bg-red-500 text-white';
  };

  const handleDateClick = (dateStr) => {
    // Navigate to ViewOrders page with the selected date
    navigate('/view-orders', { 
      state: { 
        selectedDate: dateStr,
        alert: { type: "success", message: `Showing orders for ${new Date(dateStr).toLocaleDateString()}` }
      } 
    });
  };

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">Order Calendar</h3>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => handleMonthChange('prev')}
            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="font-medium text-gray-700 min-w-[120px] text-center">
            {new Date(calendarMonth.year, calendarMonth.month).toLocaleString('default', { month: 'long', year: 'numeric' })}
          </span>
          <button 
            onClick={() => handleMonthChange('next')}
            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-xs">
        {/* Day headers */}
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
          <div key={day} className="font-medium text-gray-500 text-center py-2">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {(() => {
          const days = [];
          const firstDay = getFirstDayOfWeek(calendarMonth.year, calendarMonth.month);
          const numDays = getDaysInMonth(calendarMonth.year, calendarMonth.month);
          
          // Add empty cells for days before the first day of the month
          for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="h-8"></div>);
          }
          
          // Add days of the month
          for (let day = 1; day <= numDays; day++) {
            const dateStr = formatDate(calendarMonth.year, calendarMonth.month, day);
            const count = orderCounts[dateStr] || 0;
            const isToday = dateStr === new Date().toISOString().slice(0, 10);
            
                         days.push(
               <button 
                 key={dateStr} 
                 onClick={() => handleDateClick(dateStr)}
                 className={`relative h-8 rounded-lg flex items-center justify-center text-xs font-medium transition-colors cursor-pointer border border-gray-200 ${
                   isToday 
                     ? 'bg-blue-100 text-blue-700 border-2 border-blue-300 hover:bg-blue-200' 
                     : 'hover:bg-gray-100'
                 }`}
               >
                 <span className="z-10">{day}</span>
                 {count > 0 && (
                   <div className={`absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm px-1 ${getOrderCountColor(count)}`}>
                     {count}
                   </div>
                 )}
               </button>
             );
          }
          
          return days;
        })()}
      </div>

      {orderCountsLoading && (
        <div className="text-center py-2">
          <p className="text-xs text-gray-400">Loading order counts...</p>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <div className="flex flex-col items-center gap-2 text-xs">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-gray-600">1-3 active orders</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span className="text-gray-600">4-6 active orders</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-gray-600">7+ active orders</span>
            </div>
          </div>
          <div className="text-gray-500 italic">• Click any date to view orders</div>
        </div>
      </div>
    </div>
  );
} 